import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export type DefaultplusServiceWindowDay =
  | 'everyday'
  | 'weekdays'
  | 'weekend'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export interface DefaultplusServiceTimeWindow {
  dayRange: DefaultplusServiceWindowDay;
  startTime: string | null;
  endTime: string | null;
}

export interface DefaultplusService {
  id?: number;
  name: string;
  durationMin: number;
  parallel: number;
  // dp fittings knobs (migration 030) — absent keys fall back to column
  // defaults backend-side, so older payload shapes stay valid
  meetingEnabled?: boolean;
  askAttendees?: boolean;
  priceText?: string | null;
  // null = unrestricted. Empty times are legal only on a specific weekday
  // and mean the service is not offered that day (SPEC V42).
  timeWindows?: DefaultplusServiceTimeWindow[] | null;
}

/**
 * Client for the defaultPlus service catalog
 * (Back_End_Server /api/v1/defaultplus-services, migration 029).
 *
 * JWT-authed — the AuthInterceptor attaches the token for every
 * environment.baseUrl request; the tenant always comes from the token.
 * The page saves the whole catalog at once. Existing IDs are sent back so the
 * backend can reconcile in place and preserve staff-service join rows.
 */
@Injectable({ providedIn: 'root' })
export class DefaultplusServicesService {
  private readonly apiUrl = `${environment.baseUrl}/api/v1/defaultplus-services`;

  constructor(private http: HttpClient) {}

  /** The logged-in tenant's service catalog in page order. */
  getServices(): Observable<DefaultplusService[]> {
    return this.http
      .get<{ message: string; data: DefaultplusService[] }>(this.apiUrl)
      .pipe(map((res) => res.data || []));
  }

  /**
   * A tenant's catalog for the guest booking/edit pages. No JWT — the tenant
   * is addressed by user_id, matching the public booking-form-settings route.
   */
  getPublicServices(userId: string | number): Observable<DefaultplusService[]> {
    return this.http
      .get<{ message: string; data: DefaultplusService[] }>(
        `${this.apiUrl}/public/${userId}`
      )
      .pipe(map((res) => res.data || []));
  }

  /** Replace the whole catalog. Empty array clears it. */
  saveServices(
    services: DefaultplusService[]
  ): Observable<DefaultplusService[]> {
    return this.http
      .put<{ message: string; data: DefaultplusService[] }>(this.apiUrl, {
        services: services.map((service) => ({
          ...(service.id != null ? { id: service.id } : {}),
          name: service.name,
          durationMin: service.durationMin,
          parallel: service.parallel,
          meetingEnabled: service.meetingEnabled === true,
          askAttendees: service.askAttendees === true,
          priceText: service.priceText ?? null,
          timeWindows: service.timeWindows ?? null,
        })),
      })
      .pipe(map((res) => res.data || []));
  }
}
